import * as turf from '../utils/turf';
import classNames from 'classnames';
import '../../styles/components/overlay.scss';

/**
 * Returns the minimum area required for a label to be visible at
 * the given zoom level.  Returns 0 when any area is acceptable.
 */
function minAreaForZoom(zoom, ignoreArea) {
    if (zoom < 4)  return 50000000000;
    if (zoom < 5)  return 10000000000;
    if (zoom < 7)  return 1000000000;
    if (zoom < 8)  return 200000000;
    if (zoom < 9)  return 100000000;
    if (zoom < 10) return 100000000;
    if (zoom < 11) return 10000000;
    if (zoom < 12) return 5000000;
    if (ignoreArea) return 0;
    if (zoom < 13) return 1000000;
    if (zoom < 14) return 500000;
    if (zoom < 15) return 100000;
    if (zoom < 16) return 50000;
    return 0;
}

const Overlay = ({polygons, map, maps, ignoreArea = false, center = null, modifiers}) => {
    class AreaOverlay extends maps.OverlayView {
        constructor() {
            super();
            this.pane = null;
            this.maps = maps;
            this.labelOverlay = document.createElement('div');
            this.labelOverlay.className = 'overlay-container';
        }

        getCachedPoint(featureData) {
            if (featureData._cachedPoint) return featureData._cachedPoint;
            const pt = turf.pointOnFeature(featureData.polygon);
            featureData._cachedPoint = pt;
            return pt;
        }

        onAdd() {
            if (!polygons) return;
            
            const map = this.getMap();
            if (!map) return;

            this.pane = this.getPanes();
            this.labelOverlay.style.left = '0';
            this.labelOverlay.style.top = '0';
            this.labelOverlay.style.width = '100%';
            this.labelOverlay.style.height = '100%';
            this.labelOverlay.style.pointerEvents = 'none';
            this.pane.overlayMouseTarget.appendChild(this.labelOverlay);
            
            const zoom = map.getZoom();
            const bounds = map.getBounds();
            const threshold = minAreaForZoom(zoom, ignoreArea);
            const fragment = document.createDocumentFragment();

            for (const featureData of polygons) {
                let label = null;
                if (featureData.feature.geometry.type === 'GeometryCollection') {
                    if (featureData.feature.properties.homeMarkerGeoJson) {
                        const labelPos = featureData.feature.properties.homeMarkerGeoJson[0];
                        label = this.createLabel(featureData.feature, labelPos, bounds, modifiers);
                        if (label) fragment.appendChild(label);
                    } else {
                        for (const polygon of featureData.feature.geometry.geometries) {
                            const area = turf.area(polygon);
                            if (area < threshold) continue;
                            const labelPos = turf.pointOnFeature(polygon);
                            label = this.createLabel(featureData.feature, labelPos, bounds, modifiers);
                            if (label) fragment.appendChild(label);
                        }
                    }
                } else {
                    const area = featureData.feature.properties.area ?? 0;
                    if (area && area < threshold) continue;

                    if (featureData.feature.properties.homeMarkerGeoJson) {
                        const labelPos = featureData.feature.properties.homeMarkerGeoJson[0];
                        label = this.createLabel(featureData.feature, labelPos, bounds, modifiers);
                        if (label) fragment.appendChild(label);
                    } else {
                        const labelPos = this.getCachedPoint(featureData);
                        const position = this.toLatLng(labelPos.geometry.coordinates);
                        if (!ignoreArea && !bounds.contains(position)) continue;
                        label = this.createLabelAtPixel(featureData.feature, position, modifiers);
                        if (label) fragment.appendChild(label);
                    }
                }
            }
           
            this.labelOverlay.append(fragment);
        }

        createLabel(feature, labelPos, bounds, modifiers) {
            const position = this.toLatLng(labelPos.geometry.coordinates);
            if (!ignoreArea && !bounds.contains(position)) return null;
            return this.createLabelAtPixel(feature, position, modifiers);
        }

        createLabelAtPixel(feature, position, modifiers) {
            const overlayProjection = this.getProjection();
            const nw = overlayProjection.fromLatLngToDivPixel(position);

            const div = document.createElement('div');
            div.style.position = 'absolute';
            div.innerHTML = feature.properties.name;
            div.className = classNames({
                'overlay-label': true,
                ...modifiers
            });
            div.style.left = nw.x + "px";
            div.style.top = nw.y + "px";
            div._anchorLatLng = position;

            return div;
        }

        draw() {
            const map = this.getMap();
            if (!map) return;

            const overlayProjection = this.getProjection();
            if (!overlayProjection || !this.labelOverlay) return;

            const children = this.labelOverlay.children;
            for (let i = 0; i < children.length; i++) {
                const div = children[i];
                if (div._anchorLatLng) {
                    const nw = overlayProjection.fromLatLngToDivPixel(div._anchorLatLng);
                    div.style.left = nw.x + 'px';
                    div.style.top = nw.y + 'px';
                }
            }
        }

        onRemove() {
            if (this.labelOverlay) {
                if (this.labelOverlay.parentNode) {
                    this.labelOverlay.parentNode.removeChild(this.labelOverlay);
                }
                this.labelOverlay = null;
            }
        }

        remove() {
            this.setMap(null);
        }

        toLatLng(point) {
            return new this.maps.LatLng(point[1], point[0]);
        }
    }

    const lblOverlay = new AreaOverlay();
    lblOverlay.setMap(map);
    return lblOverlay;
};
export default Overlay;